import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts'
import Button from '@cloudscape-design/components/button'
import SpaceBetween from '@cloudscape-design/components/space-between'
import Box from '@cloudscape-design/components/box'
import {
  colorTextBodyDefault,
  colorTextBodySecondary,
  colorBackgroundContainerContent,
  colorBackgroundContainerHeader,
  colorBorderDividerDefault,
  colorTextStatusInfo,
  colorTextStatusSuccess,
  colorTextStatusError,
} from '@cloudscape-design/design-tokens'
import { useAppStore } from '@/store/app-store'
import { useDesignTokens, SERIES_PALETTE } from '@/hooks/useDesignTokens'
import { buildCloudHeaders } from '@/engine/api-client'
import { API_BASE } from '@/config/api'

const COLORS = SERIES_PALETTE

/** Aggregated metric row returned by /global-metrics */
interface GlobalMetricRow {
  modelId: string
  region: string
  period: string
  avgTtft: number
  p50Ttft: number
  p95Ttft: number
  p99Ttft: number
  avgThroughput: number
  avgTotalTime: number
  p50TotalTime: number
  p95TotalTime: number
  p99TotalTime: number
  count: number
}

/** Trend data point returned by /global-trends */
interface GlobalTrendPoint {
  timestamp: number
  modelId: string
  region: string
  ttftSec: number
  totalTimeSec: number
  tokenThroughput: number
  source: string
}

type TrendMetric = 'ttftSec' | 'tokenThroughput' | 'totalTimeSec'

export function GlobalMetricsPage() {
  const [metrics, setMetrics] = useState<GlobalMetricRow[]>([])
  const [trends, setTrends] = useState<GlobalTrendPoint[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [modelFilter, setModelFilter] = useState('')
  const [regionFilter, setRegionFilter] = useState('')
  const [trendMetric, setTrendMetric] = useState<TrendMetric>('ttftSec')

  const isAuthenticated = useAppStore(s => s.isAuthenticated)
  const tokens = useDesignTokens()

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const headers = await buildCloudHeaders()
      const params = new URLSearchParams()
      if (modelFilter) params.set('modelId', modelFilter)
      if (regionFilter) params.set('region', regionFilter)
      // Default to 'all' source if no filters specified (API requires at least one)
      if (!modelFilter && !regionFilter) params.set('source', 'all')

      // Both endpoints require from/to epoch ms
      const now = Date.now()
      const fromMs = now - 7 * 24 * 60 * 60 * 1000 // default last 7 days
      params.set('from', String(fromMs))
      params.set('to', String(now))

      const [metricsRes, trendsRes] = await Promise.all([
        fetch(`${API_BASE}/global-metrics?${params}`, { headers }),
        fetch(`${API_BASE}/global-trends?${params}`, { headers }),
      ])

      if (metricsRes.ok) {
        const data = await metricsRes.json()
        setMetrics(Array.isArray(data) ? data : data.items ?? [])
      } else {
        console.warn('[GlobalMetricsPage] Metrics fetch failed:', metricsRes.status)
        setMetrics([])
      }

      if (trendsRes.ok) {
        const data = await trendsRes.json()
        setTrends(Array.isArray(data) ? data : data.items ?? [])
      } else {
        console.warn('[GlobalMetricsPage] Trends fetch failed:', trendsRes.status)
        setTrends([])
      }
    } catch (err) {
      console.warn('[GlobalMetricsPage] Fetch failed:', err)
      setError('Failed to load global metrics. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [modelFilter, regionFilter])

  useEffect(() => {
    if (isAuthenticated) fetchData()
    else setLoading(false)
  }, [fetchData, isAuthenticated])

  // Build trend chart data grouped by modelId
  const trendChartData = useMemo(() => {
    const byTime = new Map<number, Record<string, { sum: number; count: number }>>()
    for (const pt of trends) {
      const epoch = Math.floor(pt.timestamp / 60000) * 60000
      if (!byTime.has(epoch)) byTime.set(epoch, {})
      const bucket = byTime.get(epoch)!
      const key = pt.modelId
      if (!bucket[key]) bucket[key] = { sum: 0, count: 0 }
      bucket[key].sum += pt[trendMetric]
      bucket[key].count += 1
    }
    return Array.from(byTime.entries())
      .map(([epoch, models]) => {
        const point: Record<string, unknown> = { epoch }
        for (const [name, { sum, count }] of Object.entries(models)) {
          point[name] = Number((sum / count).toFixed(3))
        }
        return point
      })
      .sort((a, b) => (a.epoch as number) - (b.epoch as number))
  }, [trends, trendMetric])

  const modelNames = useMemo(() => {
    const names = new Set<string>()
    for (const pt of trends) names.add(pt.modelId)
    return Array.from(names)
  }, [trends])

  // Latest scheduled run results for health overview
  const latestScheduledResults = useMemo(() => {
    const scheduledPoints = trends.filter(pt => pt.source === 'scheduler')
    const byModel = new Map<string, GlobalTrendPoint>()
    for (const pt of scheduledPoints) {
      const existing = byModel.get(pt.modelId)
      if (!existing || pt.timestamp > existing.timestamp) {
        byModel.set(pt.modelId, pt)
      }
    }
    return Array.from(byModel.values()).sort((a, b) => b.timestamp - a.timestamp)
  }, [trends])

  const formatTick = (epoch: number) => {
    const d = new Date(epoch)
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
  }

  const metricLabels: Record<string, string> = {
    ttftSec: 'Avg TTFT (s)',
    tokenThroughput: 'Avg Throughput (tok/s)',
    totalTimeSec: 'Avg Total Time (s)',
  }

  if (!isAuthenticated) {
    return (
      <div style={{ padding: 24 }}>
        <h2 style={{ color: colorTextBodyDefault, margin: '0 0 16px', fontSize: 20 }}>Global Metrics</h2>
        <Box textAlign="center" padding={{ vertical: 'xxl' }}>
          <Box variant="h3" color="text-body-secondary">Sign in to view global metrics</Box>
          <Box color="text-body-secondary">Global metrics requires authentication.</Box>
          <Button variant="primary" onClick={() => useAppStore.getState().setPage('signin')}>Sign In</Button>
        </Box>
      </div>
    )
  }

  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ color: colorTextBodyDefault, margin: '0 0 16px', fontSize: 20 }}>Global Metrics</h2>

      {/* Filters */}
      <div style={styles.card}>
        <SpaceBetween direction="horizontal" size="s">
          <div>
            <label style={styles.label}>Model</label>
            <input
              type="text"
              placeholder="Filter by model ID..."
              style={{ ...styles.select, width: 240 }}
              value={modelFilter}
              onChange={e => setModelFilter(e.target.value)}
            />
          </div>
          <div>
            <label style={styles.label}>Region</label>
            <input
              type="text"
              placeholder="Filter by region..."
              style={{ ...styles.select, width: 180 }}
              value={regionFilter}
              onChange={e => setRegionFilter(e.target.value)}
            />
          </div>
          <div style={{ alignSelf: 'flex-end' }}>
            <Button variant="primary" onClick={fetchData}>Apply</Button>
          </div>
          <div style={{ alignSelf: 'flex-end' }}>
            <Button variant="normal" onClick={() => { setModelFilter(''); setRegionFilter('') }}>Clear</Button>
          </div>
        </SpaceBetween>
      </div>

      {/* Loading */}
      {loading && (
        <Box textAlign="center" padding={{ vertical: 'l' }}>
          <Box color="text-body-secondary">Loading global metrics...</Box>
        </Box>
      )}

      {/* Error */}
      {error && (
        <div style={{ ...styles.card, borderColor: colorTextStatusError }}>
          <Box color="text-status-error">{error}</Box>
        </div>
      )}

      {/* Trend Charts */}
      {!loading && trendChartData.length > 0 && (
        <div style={styles.card}>
          <SpaceBetween direction="horizontal" size="xs">
            <Box variant="small" color="text-body-secondary">{metricLabels[trendMetric]} Over Time</Box>
            <Box variant="small" color="text-body-secondary">Metric:</Box>
            {(['ttftSec', 'tokenThroughput', 'totalTimeSec'] as const).map(key => (
              <Button key={key} variant={trendMetric === key ? 'primary' : 'normal'} onClick={() => setTrendMetric(key)}>
                {metricLabels[key]}
              </Button>
            ))}
          </SpaceBetween>
          <Box variant="small" color="text-body-secondary">
            {trendChartData.length} data points · {trends.length} results
          </Box>
          <ResponsiveContainer width="100%" height={360}>
            <LineChart data={trendChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke={tokens.gridColor} />
              <XAxis
                dataKey="epoch"
                type="number"
                scale="time"
                domain={['dataMin', 'dataMax']}
                tickFormatter={formatTick}
                tick={{ fill: tokens.axisLabelColor, fontSize: 11 }}
              />
              <YAxis tick={{ fill: tokens.axisLabelColor, fontSize: 11 }} />
              <Tooltip
                contentStyle={{ backgroundColor: tokens.tooltipBg, border: `1px solid ${tokens.gridColor}`, borderRadius: 6 }}
                labelFormatter={(epoch: number) => new Date(epoch).toLocaleString()}
              />
              <Legend />
              {modelNames.map((name, i) => (
                <Line key={name} type="monotone" dataKey={name} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={{ r: 3 }} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Aggregated Metrics Table (p50/p95/p99) */}
      {!loading && metrics.length > 0 && (
        <div style={styles.card}>
          <div style={{ fontSize: 15, fontWeight: 600, color: colorTextBodyDefault, marginBottom: 12 }}>
            Aggregated Metrics ({metrics.length} entries)
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr>
                  <th style={styles.th}>Model</th>
                  <th style={styles.th}>Region</th>
                  <th style={styles.th}>Avg TTFT</th>
                  <th style={styles.th}>p50 TTFT</th>
                  <th style={styles.th}>p95 TTFT</th>
                  <th style={styles.th}>p99 TTFT</th>
                  <th style={styles.th}>Avg Throughput</th>
                  <th style={styles.th}>Count</th>
                </tr>
              </thead>
              <tbody>
                {metrics.map((row, i) => (
                  <tr key={i}>
                    <td style={styles.td}>{row.modelId}</td>
                    <td style={styles.td}>{row.region || '-'}</td>
                    <td style={styles.td}>{row.avgTtft.toFixed(3)}s</td>
                    <td style={styles.td}>{row.p50Ttft.toFixed(3)}s</td>
                    <td style={styles.td}>{row.p95Ttft.toFixed(3)}s</td>
                    <td style={styles.td}>{row.p99Ttft.toFixed(3)}s</td>
                    <td style={styles.td}>{row.avgThroughput.toFixed(1)} tok/s</td>
                    <td style={styles.td}>{row.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Latest Scheduled Run Results - Model Health */}
      {!loading && latestScheduledResults.length > 0 && (
        <div style={styles.card}>
          <div style={{ fontSize: 15, fontWeight: 600, color: colorTextBodyDefault, marginBottom: 12 }}>
            Latest Scheduled Runs - Model Health
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr>
                  <th style={styles.th}>Model</th>
                  <th style={styles.th}>Region</th>
                  <th style={styles.th}>TTFT (s)</th>
                  <th style={styles.th}>Throughput</th>
                  <th style={styles.th}>Total (s)</th>
                  <th style={styles.th}>Last Run</th>
                  <th style={styles.th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {latestScheduledResults.map((pt, i) => {
                  const age = Date.now() - pt.timestamp
                  const isRecent = age < 30 * 60 * 1000 // within 30 min
                  return (
                    <tr key={i}>
                      <td style={styles.td}>{pt.modelId}</td>
                      <td style={styles.td}>{pt.region || '-'}</td>
                      <td style={styles.td}>{pt.ttftSec.toFixed(3)}</td>
                      <td style={styles.td}>{pt.tokenThroughput.toFixed(1)} tok/s</td>
                      <td style={styles.td}>{pt.totalTimeSec.toFixed(3)}</td>
                      <td style={styles.td}>{new Date(pt.timestamp).toLocaleString()}</td>
                      <td style={styles.td}>
                        <span style={{ color: isRecent ? colorTextStatusSuccess : colorTextStatusInfo }}>
                          {isRecent ? '● Healthy' : '○ Stale'}
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && metrics.length === 0 && trends.length === 0 && (
        <Box textAlign="center" padding={{ vertical: 'xxl' }}>
          <SpaceBetween size="m">
            <Box variant="h3" color="text-body-secondary">No global metrics yet</Box>
            <Box color="text-body-secondary">
              Global metrics are populated from cloud-mode and scheduled benchmark runs.
            </Box>
          </SpaceBetween>
        </Box>
      )}
    </div>
  )
}

const styles: Record<string, React.CSSProperties> = {
  card: {
    backgroundColor: colorBackgroundContainerContent,
    borderRadius: 10,
    padding: 20,
    border: `1px solid ${colorBorderDividerDefault}`,
    marginBottom: 16,
  },
  label: { display: 'block', fontSize: 11, color: colorTextBodySecondary, marginBottom: 4 },
  select: {
    padding: '6px 10px',
    backgroundColor: colorBackgroundContainerHeader,
    border: `1px solid ${colorBorderDividerDefault}`,
    borderRadius: 4,
    color: colorTextBodyDefault,
    fontSize: 12,
  },
  th: {
    textAlign: 'left' as const,
    padding: '6px 8px',
    color: colorTextBodySecondary,
    borderBottom: `1px solid ${colorBorderDividerDefault}`,
    fontWeight: 500,
  },
  td: {
    padding: '6px 8px',
    color: colorTextBodySecondary,
    borderBottom: `1px solid ${colorBorderDividerDefault}`,
  },
}
